// Shared invoice-line-items table.
//
// Used by every supplier-import surface (ASS positions modal, future Brünning /
// Turan / Winfood views) AND eventually by the Auswertung invoice drill-down,
// so the CEO sees the same chrome — search · Bio filter · MwSt filter · sticky
// sortable rows · same BIO/Anbruch/Pfand-Rückgabe pills — regardless of which
// supplier produced the data.
//
// Contract: caller provides a flat array of rows (`InvoiceLineItemRow`) and
// declares which optional columns are present. Missing fields render as "–".
//
// Filters live INSIDE the table component so adding a new supplier means
// passing data, not re-implementing the UX.

import { useMemo, useState } from 'react'
import { TextInput } from '../../../../shared/ui/TextInput'
import { Segmented } from '../../../../shared/ui/Segmented'
import { Sensitive } from '../../../../shared/ui/Sensitive'

/** Canonical Bio indicator — green Material `eco` leaf in a tertiary-container
 *  circle, the same shape that was already in the Auswertung view. Defined
 *  once here so every supplier surface renders the identical marker. */
function BioLeaf() {
  return (
    <span
      title="Bio-Produkt"
      className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-tertiary-container/60 text-on-tertiary-container align-middle"
      aria-label="Bio"
    >
      <span className="material-symbols-outlined !text-[12px]" aria-hidden="true">eco</span>
    </span>
  )
}

export interface InvoiceLineItemRow {
  /** React key — must be unique within the rows[] array. */
  id: string
  articleId?: string | null
  description: string
  /** Optional product taxonomy (ChefsCulinar). */
  category?: string | null
  quantity?: string | null
  unit?: string | null
  unitPrice?: string | null
  lineTotal: string
  /** '7.00', '19.00', etc. */
  taxRate?: string | null
  /** 'ware' | 'pfand' | 'other' | 'control' | null */
  taxCategory?: string | null
  isBio?: boolean
  isAnbruch?: boolean
  isPfandReturn?: boolean
  /** false = ordered outside the supplier's current Preiskatalog; null/undefined = not checked. */
  inCatalog?: boolean | null
}

export type LineItemColumn =
  | 'artikel'
  | 'bezeichnung'
  | 'kategorie'
  | 'menge'
  | 'preis'
  | 'mwst'
  | 'gesamt'

const DEFAULT_COLUMNS: readonly LineItemColumn[] = [
  'artikel', 'bezeichnung', 'menge', 'preis', 'gesamt', 'mwst',
]

const COL_HEADER: Record<LineItemColumn, string> = {
  artikel:     'Artikel',
  bezeichnung: 'Bezeichnung',
  kategorie:   'Kategorie',
  menge:       'Menge',
  preis:       'Preis',
  gesamt:      'Gesamt',
  mwst:        'MwSt',
}

type BioFilter  = 'all' | 'bio' | 'non-bio'
type CatFilter  = 'all' | 'ware' | 'other' | 'pfand'

function formatEUR(s: string | null | undefined): string {
  if (s == null || s === '') return '–'
  const n = parseFloat(s)
  if (!isFinite(n)) return s
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
}

interface Props {
  rows: InvoiceLineItemRow[]
  /** Columns rendered in this order. Defaults to the standard 6. */
  columns?: readonly LineItemColumn[]
  /** Override the row-count summary on the right. */
  summaryLabel?: string
  /** Disable the search input (e.g. tiny lists). */
  hideSearch?: boolean
  /** Hide MwSt filter (e.g. supplier has no tax breakdown). */
  hideMwstFilter?: boolean
  /** Hide ALL filter chrome (search + bio + mwst). Used when the parent
   *  already provides filters (e.g. Auswertung drill-down). */
  hideFilters?: boolean
  /** Hide the row-count + filtered-sum strip above the table. */
  hideSummary?: boolean
  /** Cap the table height. Defaults to 60vh (page-modal use); pass a smaller
   *  Tailwind class (e.g. "max-h-96") when embedding inside accordions. */
  maxHeightClass?: string
}

export function InvoicePositionsTable({
  rows,
  columns = DEFAULT_COLUMNS,
  summaryLabel,
  hideSearch = false,
  hideMwstFilter = false,
  hideFilters = false,
  hideSummary = false,
  maxHeightClass = 'max-h-[60vh]',
}: Props) {
  const [search, setSearch] = useState('')
  const [bioFilter, setBioFilter] = useState<BioFilter>('all')
  const [catFilter, setCatFilter] = useState<CatFilter>('all')

  const filtered = useMemo(() => rows.filter(r => {
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      if (!r.description.toLowerCase().includes(q) &&
          !(r.articleId ?? '').toLowerCase().includes(q)) return false
    }
    if (bioFilter === 'bio'     && !r.isBio) return false
    if (bioFilter === 'non-bio' &&  r.isBio) return false
    if (catFilter !== 'all' && r.taxCategory !== catFilter) return false
    return true
  }), [rows, search, bioFilter, catFilter])

  const filteredSum = useMemo(
    () => filtered.reduce((s, r) => s + parseFloat(r.lineTotal || '0'), 0),
    [filtered],
  )
  const bioCount = useMemo(() => rows.filter(r => r.isBio).length, [rows])
  const offCatalogCount = useMemo(() => rows.filter(r => r.inCatalog === false).length, [rows])

  // Per-(taxCategory, taxRate) subtotals — same buckets shown in the MwSt
  // chip column, so the user can verify each chunk at a glance.
  const subtotals = useMemo(() => {
    const map = new Map<string, { category: string|null|undefined; taxRate: string|null|undefined; sum: number; count: number }>()
    for (const r of filtered) {
      const key = `${r.taxCategory ?? '–'}|${r.taxRate ?? '–'}`
      const cur = map.get(key)
      if (cur) {
        cur.sum += parseFloat(r.lineTotal || '0')
        cur.count += 1
      } else {
        map.set(key, { category: r.taxCategory, taxRate: r.taxRate, sum: parseFloat(r.lineTotal || '0'), count: 1 })
      }
    }
    return Array.from(map.values()).sort((a, b) => Math.abs(b.sum) - Math.abs(a.sum))
  }, [filtered])

  return (
    <div className="space-y-3">
      {/* Filter chrome */}
      {!hideFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {!hideSearch && (
            <div className="flex-1 min-w-[14rem]">
              <TextInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suchen nach Bezeichnung oder Artikel-Nr…"
                variant="glass"
                size="default"
              />
            </div>
          )}
          <Segmented
            value={bioFilter}
            onChange={setBioFilter}
            options={[
              { value: 'all', label: 'Alle' },
              { value: 'bio', label: 'Nur Bio', icon: 'eco' },
              { value: 'non-bio', label: 'Ohne Bio' },
            ]}
          />
          {!hideMwstFilter && (
            <Segmented
              value={catFilter}
              onChange={setCatFilter}
              options={[
                { value: 'all', label: 'Alle MwSt' },
                { value: 'ware', label: 'Ware 7 %' },
                { value: 'other', label: 'Ware 19 %' },
                { value: 'pfand', label: 'Pfand 19 %' },
              ]}
            />
          )}
        </div>
      )}

      {/* Result summary */}
      {!hideSummary && (
        <div className="flex items-start justify-between gap-3 text-[11px] text-on-surface-variant flex-wrap">
          <span>
            <span className="font-semibold text-on-surface tabular-nums">{filtered.length}</span> von {rows.length} Zeilen
            {bioCount > 0 && <> · <span className="font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums">{bioCount}</span> Bio</>}
            {offCatalogCount > 0 && <> · <span className="font-semibold text-amber-700 dark:text-amber-300 tabular-nums">{offCatalogCount}</span> nicht im Preiskatalog</>}
          </span>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {subtotals.length > 1 && subtotals.map((s, i) => (
              <span
                key={i}
                className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-mono ${
                  s.category === 'ware'  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' :
                  s.category === 'pfand' ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300' :
                  s.category === 'other' ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300' :
                                           'bg-surface-container text-on-surface-variant'
                }`}
              >
                <span>{s.category ?? '–'} · {s.taxRate ?? '–'}%</span>
                <Sensitive>{formatEUR(s.sum.toFixed(2))}</Sensitive>
              </span>
            ))}
            <span>
              {summaryLabel ?? 'Summe'}:{' '}
              <span className="font-mono font-semibold text-on-surface">
                <Sensitive>{formatEUR(filteredSum.toFixed(2))}</Sensitive>
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest/40 overflow-hidden">
        <div className={`overflow-x-auto ${maxHeightClass} overflow-y-auto`}>
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface-container/80 backdrop-blur-md z-10 border-b border-outline-variant/30">
              <tr className="text-left text-on-surface-variant uppercase tracking-wider">
                {columns.map(c => (
                  <th
                    key={c}
                    className={`px-3 py-2 font-bold ${
                      c === 'menge' || c === 'preis' || c === 'gesamt' ? 'text-right' : ''
                    }`}
                  >
                    {COL_HEADER[c]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20">
              {filtered.length === 0 && (
                <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-on-surface-variant italic">
                  Keine Positionen passen zum Filter.
                </td></tr>
              )}
              {filtered.map(r => {
                const total = parseFloat(r.lineTotal || '0')
                const isNeg = isFinite(total) && total < 0
                return (
                  <tr key={r.id} className="hover:bg-surface-container-low/50 transition-colors">
                    {columns.map(c => {
                      switch (c) {
                        case 'artikel':
                          return <td key={c} className="px-3 py-2 font-mono text-on-surface-variant whitespace-nowrap">{r.articleId || '–'}</td>
                        case 'bezeichnung':
                          return (
                            <td key={c} className="px-3 py-2">
                              <span className="text-on-surface align-middle">{r.description}</span>
                              {r.isBio && <BioLeaf />}
                              {r.isAnbruch && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 px-1.5 py-0.5 text-[10px]">Anbruch</span>}
                              {r.isPfandReturn && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-sky-500/15 text-sky-700 dark:text-sky-300 border border-sky-500/30 px-1.5 py-0.5 text-[10px]">Pfand-Rückgabe</span>}
                              {r.inCatalog === false && <span title="Artikel hat keinen aktuellen Preis im Preiskatalog des Lieferanten" className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 px-1.5 py-0.5 text-[10px]"><span className="material-symbols-outlined !text-[11px]" aria-hidden="true">error_outline</span>nicht im Preiskatalog</span>}
                            </td>
                          )
                        case 'kategorie':
                          return <td key={c} className="px-3 py-2 text-on-surface-variant">{r.category || <span className="italic opacity-60">–</span>}</td>
                        case 'menge':
                          return <td key={c} className="px-3 py-2 font-mono tabular-nums text-right text-on-surface">{r.quantity ?? '–'} <span className="text-on-surface-variant text-[10px]">{r.unit ?? ''}</span></td>
                        case 'preis':
                          return <td key={c} className="px-3 py-2 font-mono tabular-nums text-right text-on-surface"><Sensitive>{formatEUR(r.unitPrice)}</Sensitive></td>
                        case 'gesamt':
                          return (
                            <td key={c} className={`px-3 py-2 font-mono tabular-nums text-right font-semibold ${isNeg ? 'text-rose-700 dark:text-rose-300' : 'text-on-surface'}`}>
                              <Sensitive>{formatEUR(r.lineTotal)}</Sensitive>
                            </td>
                          )
                        case 'mwst':
                          return (
                            <td key={c} className="px-3 py-2 whitespace-nowrap">
                              {(r.taxCategory || r.taxRate) ? (
                                <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-mono ${
                                  r.taxCategory === 'ware'    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' :
                                  r.taxCategory === 'pfand'   ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300' :
                                  r.taxCategory === 'other'   ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300' :
                                                                'bg-surface-container text-on-surface-variant'
                                }`}>{r.taxCategory ? `${r.taxCategory} · ` : ''}{r.taxRate ? `${r.taxRate}%` : ''}</span>
                              ) : '–'}
                            </td>
                          )
                        default:
                          return null
                      }
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
